/**
 * BlogListPage
 * 
 * Main blog page displaying all posts with filtering and search.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../../components/SEOHelmet';
import { BlogConfig } from '../BlogConfig';
import { loadAllPosts, searchPosts } from '../utils/postLoader';
import PostCard from '../components/PostCard';
import BottomNav from '../components/BottomNav';
import Config from '../../Config';
import { useLanguage } from '../../i18n/LanguageContext';

const BlogListPage = () => {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const localeMap = { en: 'en-US', pt: 'pt-BR', fr: 'fr-FR' };
        const loadedPosts = await loadAllPosts(localeMap[language]);
        setPosts(loadedPosts);
        setFilteredPosts(loadedPosts);
      } catch (err) {
        console.error('Error loading posts:', err);
        setError(t('blog.failedToLoad'));
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [language, t]);

  const filterPosts = useCallback(async () => {
    let result = [...posts];
    if (selectedCategory !== 'all') {
      result = result.filter(post => post.category === selectedCategory);
    }
    if (searchQuery.trim()) {
      const searchResults = await searchPosts(searchQuery);
      const searchSlugs = new Set(searchResults.map(p => p.slug));
      result = result.filter(post => searchSlugs.has(post.slug));
    }
    setFilteredPosts(result);
  }, [posts, searchQuery, selectedCategory]);

  useEffect(() => {
    filterPosts();
  }, [filterPosts]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{error}</h2>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHelmet
        title={BlogConfig.title}
        description={BlogConfig.seo.defaultDescription}
        pathname="/blog"
        type="blog"
      />

      <div className="min-h-screen bg-gray-50">
        {/* Sticky Header - Same style as BlogPostPage */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              {/* Left: Back to Home */}
              <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="text-sm font-medium">{t('nav.home')}</span>
              </Link>

              {/* Center: Blog Title & Subtitle */}
              <div className="hidden sm:flex flex-col items-center">
                <h1 className="text-lg font-bold text-gray-900">{t('nav.blog')}</h1>
                <p className="text-xs text-gray-500">{t('blog.featuredSubtitle')}</p>
              </div>

              {/* Right: Author */}
              <Link to="/" className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900 hidden sm:block">{Config.name}</span>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center text-white text-xs font-bold">
                  {Config.initials}
                </div>
              </Link>
            </div>
          </div>
        </header>

        {/* Mobile Title (shown only on small screens) */}
        <div className="sm:hidden bg-white border-b border-gray-100 px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">{t('nav.blog')}</h1>
          <p className="text-sm text-gray-500">{t('blog.featuredSubtitle')}</p>
        </div>

        {/* Search & Filters Bar */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              {/* Search */}
              {BlogConfig.features.enableSearch && (
                <div className="relative flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder={t('blog.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2.5 pl-10 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
                  />
                  <svg className="absolute left-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              )}

              {/* Category Pills */}
              {BlogConfig.features.enableCategories && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === 'all'
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {t('blog.all')}
                  </button>
                  {BlogConfig.categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedCategory === category.id
                          ? 'text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      style={selectedCategory === category.id ? { backgroundColor: category.color } : {}}
                    >
                      {t(`blog.categories.${category.id}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('blog.noPostsFound')}</h3>
              <p className="text-gray-600 mb-4">{searchQuery ? t('blog.noPostsSubtitle') : t('blog.noPostsAvailable')}</p>
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }} className="text-blue-600 hover:text-blue-800 font-medium">
                  {t('blog.clearFilters')}
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-gray-500 mb-6 text-sm">
                {filteredPosts.length} {filteredPosts.length === 1 ? t('blog.article') : t('blog.articles')}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPosts.map((post) => (
                  <PostCard key={post.slug} post={post} />
                ))}
              </div>
            </>
          )}
        </div>

        <BottomNav />
      </div>
    </>
  );
};

export default BlogListPage;
